import React, { useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItemButton, ListItemText, Paper, TextField, Typography } from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { profilePictureApi } from '../services/profilePictureApi';

type Person = { user_id: string; display_name: string; email: string };
type Status = { consented: boolean; withdrawn_at: string | null };

const ConsentManagement: React.FC = () => {
  const { getAccessToken } = useAuth();
  const [query, setQuery] = useState('');
  const [people, setPeople] = useState<Person[]>([]);
  const [selected, setSelected] = useState<Person | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const [source, setSource] = useState('emailed ProjectSupport');
  const [confirmAction, setConfirmAction] = useState<'withdraw' | 'revert' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const search = async () => {
    if (!query.trim()) return;
    setError('');
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      setPeople(await profilePictureApi.searchConsentUsers(query.trim(), token));
      setSelected(null);
      setStatus(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Search failed');
    }
  };

  const selectPerson = async (person: Person) => {
    setError('');
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const current = await profilePictureApi.getConsentStatus(person.user_id, token);
      setSelected(person);
      setStatus(current);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Could not load consent');
    }
  };

  const submit = async () => {
    if (!selected || !confirmAction) return;
    setBusy(true);
    setError('');
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      setStatus(await profilePictureApi.updateConsent(selected.user_id, confirmAction, source, token));
      setConfirmAction(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Could not update consent');
    } finally {
      setBusy(false);
    }
  };

  return <Paper sx={{ p: 2, mb: 3 }}>
    <Typography variant="h6">Contractor consent</Typography>
    <Typography variant="body2">Find a named person to record or revert a withdrawal.</Typography>
    <Box display="flex" gap={1} mt={2}>
      <TextField label="Name or email" value={query} onChange={e => setQuery(e.target.value)} size="small" />
      <Button onClick={search} variant="outlined">Search</Button>
    </Box>
    <List dense>{people.map(person => <ListItemButton key={person.user_id} onClick={() => selectPerson(person)}>
      <ListItemText primary={person.display_name} secondary={person.email} />
    </ListItemButton>)}</List>
    {selected && status && <Box>
      <Typography>{selected.display_name} ({selected.email})</Typography>
      <Typography>Consent state: {status.withdrawn_at ? `Withdrawn on ${status.withdrawn_at}` : status.consented ? 'Consented' : 'No consent recorded'}</Typography>
      <TextField label="Source" value={source} onChange={e => setSource(e.target.value)} size="small" sx={{ mr: 1, mt: 1 }} />
      <Button onClick={() => setConfirmAction(status.withdrawn_at ? 'revert' : 'withdraw')} variant="contained" sx={{ mt: 1 }}>
        {status.withdrawn_at ? 'Revert withdrawal' : 'Record withdrawal'}
      </Button>
    </Box>}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    <Dialog open={!!confirmAction} onClose={() => setConfirmAction(null)}>
      <DialogTitle>Confirm consent change</DialogTitle>
      <DialogContent>
        {confirmAction === 'withdraw' ? 'Record withdrawal for' : 'Revert withdrawal for'} {selected?.display_name} ({selected?.email})?
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmAction(null)} disabled={busy}>Cancel</Button>
        <Button onClick={submit} disabled={busy || !source.trim()}>Confirm</Button>
      </DialogActions>
    </Dialog>
  </Paper>;
};

export default ConsentManagement;
